import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import config from "./config";
import DisclosureIcon from "./DisclosureIcon";
import { incidentPreview, incidentState, incidentLabel, incidentText, incidentReportLink } from "./serviceIncidentPresentation";
import { serviceHistoryPath } from "./serviceHistoryRoutes";

export default function MeetingServiceStatus({ serviceId, service }) {
  const [details, setDetails] = useState(null);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(false);
  useEffect(() => setExpanded(false), [serviceId]);
  useEffect(() => {
    setDetails(null); setError("");
    if (!serviceId) return;
    let cancelled = false; let fetching = false; let controller;
    async function refresh() {
      if (fetching || document.hidden) return;
      fetching = true; controller = new AbortController();
      const options = { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }, signal: controller.signal, timeout: 15000 };
      try {
        const [status, incidents] = await Promise.all([
          axios.get(`${config.API_URL}api/User/GetServiceStatus/${encodeURIComponent(serviceId)}`, options),
          axios.get(`${config.API_URL}api/User/GetServiceIncidents/${encodeURIComponent(serviceId)}`, options),
        ]);
        if (!cancelled) { setDetails({ ...status.data, incidents: incidentPreview(incidents.data) }); setError(""); }
      } catch { if (!cancelled) { setDetails(null); setError("This service's current status and incidents could not be loaded. Please retry shortly."); } }
      finally { fetching = false; }
    }
    refresh(); const timer = setInterval(refresh, 30000);
    document.addEventListener("visibilitychange", refresh);
    return () => { cancelled = true; controller?.abort(); clearInterval(timer); document.removeEventListener("visibilitychange", refresh); };
  }, [serviceId]);
  if (!serviceId) return null;
  const status = details?.CurrentStatus || "Status unavailable";
  const style = status === "Operational" ? "healthy" : status === "Outage" ? "outage" : status === "Active incident" ? "incident" : "unknown";
  // Match the backend's active-incident list: every incident that is not resolved.
  const activeIncidentCount = details?.incidents.filter(incident => incidentState(incident.Status) !== "resolved").length || 0;
  const countStyle = !details ? "unknown" : !activeIncidentCount ? "healthy"
    : style === "outage" || details.incidents.some(incident => incidentState(incident.Status) === "outage") ? "outage" : "incident";
  const displayName = details?.DisplayName || service?.ServiceDisplayName || "Meeting service";
  return <section className="service-monitor-row meeting-service" data-service-status={style} aria-label="Meeting service status and incidents">
    <div className="service-monitor-row-main">
      <div className="service-monitor-identity">
        <span className="service-monitor-logo" aria-hidden="true"><span>{displayName.slice(0, 1)}</span>
          {service?.ServiceName && <img key={service.ServiceName} src={`https://www.signaltuner.com/images/serviceicons/${encodeURIComponent(service.ServiceName)}.png`} alt="" onError={event => { event.currentTarget.style.display = "none"; }} />}
        </span>
        <div><h3>{displayName}</h3><span className={`service-monitor-status service-monitor-status--${style}`}><span aria-hidden="true" />{!details && !error ? "Loading service status..." : status}</span></div>
      </div>
      <span className={`meeting-active-incidents service-monitor-status service-monitor-status--${countStyle}`}>
        {details ? `${activeIncidentCount} active ${activeIncidentCount === 1 ? "incident" : "incidents"}` : "Incident count unavailable"}
      </span>
      <button className="service-monitor-details-button" type="button" aria-expanded={expanded} aria-controls={`meeting-service-incidents-${serviceId}`}
        disabled={!details} onClick={() => setExpanded(value => !value)}>
        {expanded ? "Hide incidents" : "View incidents"}<DisclosureIcon />
      </button>
    </div>
    {error && <p className="service-monitor-alert" role="alert">{error}</p>}
    <div className="service-monitor-incidents" id={`meeting-service-incidents-${serviceId}`} hidden={!expanded}>
      {details && <><h4>Incidents for {displayName}</h4>
        {!details.incidents.length && <p>No incidents to display.</p>}
        {details.incidents.map(incident => <article key={incident.IncidentId} data-incident-state={incidentState(incident.Status)}>
        <div className="service-monitor-incident-heading"><h5>{incident.Title}</h5><span className="service-monitor-incident-label">{incidentLabel(incident.Status)}</span></div>
        <p className="service-monitor-date">Updated {new Date(incident.PublishDate).toLocaleString()}</p>
        <p className="service-monitor-description">{incidentText(incident.Description)}</p>
        {incidentReportLink(incident.Link) && <a href={incidentReportLink(incident.Link)} target="_blank" rel="noopener noreferrer">Read provider report ↗</a>}
      </article>)}
      <Link className="service-monitor-history-link" to={serviceHistoryPath(serviceId)}>See full report history</Link>
      </>}
    </div>
  </section>;
}
